/**
 * Template lines for proactive prompts (no LLM, no latency).
 * `{name}` = ", Imię" or "", `{quest}`, `{list}`, `{rested}`, `{count}`, `{area}`, `{text}`.
 * Rules: never reproach for breaks, never moralize, short enough to be spoken.
 */
export const LINES = {
  brief: [
    "Dzień dobry{name}! Co dziś bierzemy na warsztat? Wystarczą jedna do trzech rzeczy.",
    "Hej{name}, nowy dzień. Jakie jedno do trzech zadań dziś łapiemy?",
    "Dzień dobry{name}. Jaki mamy plan na dziś?",
  ],
  briefLeftover: [
    "Dzień dobry{name}! Z wcześniej zostało: {list}. Co z tego bierzemy, a co dokładamy?",
    "Hej{name}, na liście czeka {list}. Bierzemy coś z tego dzisiaj?",
  ],
  briefRested: [
    "Dzień dobry{name}. Trochę odpoczywałem w domu i uzbierałem {rested} punktów odpoczynku. Jedna mała rzecz na dziś?",
    "Hej{name}, mam {rested} punktów odpoczynku, następne punkty liczą się podwójnie. Jaki jeden mały krok dziś?",
  ],
  briefSick: ["Dzień dobry{name}. Dziś tylko się kurujesz, ja pilnuję domu. Jak się czujesz?"],
  briefVacation: ["Dzień dobry{name}! Urlop, więc luz. Masz ochotę na coś konkretnego dziś?"],
  briefCrunch: ["Dzień dobry{name}. Wiem, że w robocie młyn. Jedna mała rzecz dla siebie na dziś?"],
  debrief: [
    "Jak minął dzień{name}? Opowiedz, co dziś ogarnąłeś, drobiazgi też się liczą.",
    "Wieczór{name}. Co dziś udało się zrobić? Każda drobnostka się liczy.",
  ],
  debriefPlan: [
    "Wieczór{name}. Na dziś zostało: {list}. Udało się coś z tego? I co jeszcze dziś ogarnąłeś?",
    "Hej{name}, na dziś było: {list}. Co z tego się udało?",
  ],
  debriefDone: [
    "Wieczór{name}. Dziś odhaczone: {done}. Dobra robota. Coś jeszcze dziś ogarnąłeś?",
    "Hej{name}, dziś zrobione: {done}. Było coś jeszcze?",
  ],
  debriefDoneLeft: ["Wieczór{name}. Odhaczone: {done}. Zostało: {left}. Udało się, czy przesuwamy na jutro?"],
  weekly: [
    "Niedziela, czas na podsumowanie{name}. W tym tygodniu {count} rzeczy, najwięcej w obszarze {area}. Czujesz progres? Oceń od 1 do 5.",
  ],
  weeklyQuiet: [
    "Niedziela{name}. Ten tydzień był spokojny i to też jest okej. Jak się z nim czujesz, od 1 do 5?",
  ],
  returnGreeting: ["Hej{name}, dobrze cię widzieć. Jak się masz?", "Wróciłem{name}. Co u ciebie słychać?"],
  chore: ["Hej{name}, {quest} czeka na dziś. Ogarnięte?", "Psst{name}, {quest}. Już zrobione?"],
  quest: ["Jak idzie z: {quest}?", "Hej{name}, jak tam {quest}?"],
  challenge: [
    "Szybkie wyzwanie: 10 pompek? Ja robię razem z tobą.",
    "Mikro wyzwanie: 15 przysiadów? Zajmie minutę.",
    "Może krótki spacer albo rozciąganie? Pięć minut wystarczy.",
  ],
  curiosity: ["Co dziś jemy{name}?", "Jak ci mija dzień{name}?", "Czytasz coś ciekawego ostatnio?"],
  reminder: ["Hej{name}, przypominam: {text}.", "Przypomnienie{name}: {text}."],
  reminderShort: ["Przypomnienie: {text}."],
  stageUp: ["Czujesz to? Rosnę w siłę razem z tobą!", "Patrz na te barki. To twoja robota!"],
  newItem: ["Patrz, w mieszkaniu przybył {item}!", "Nowy nabytek: {item}. Od razu przytulniej."],
  levelUp: ["{area} na poziomie {level}. Tak trzymaj!"],
} as const;

export type LineKey = keyof typeof LINES;

/** Fixed companion persona (stable invented preferences, SPEC 6.6). */
export const PERSONA = {
  likes: [
    "makaron z pesto i pomidorkami",
    "pierogi ruskie z cebulką",
    "zupa pomidorowa z ryżem",
    "naleśniki z twarogiem",
    "jabłka i orzechy na przekąskę",
  ],
  dislikes: ["szparagi", "bardzo ostre jedzenie"],
  hobbies: ["stare mapy", "polerowanie zbroi", "opowieści przy kominku"],
  dishes: {
    "makaron z pesto": ["makaron", "pesto", "pomidorki koktajlowe", "parmezan"],
    pierogi: ["pierogi ruskie", "cebula", "śmietana"],
    pomidorowa: ["passata pomidorowa", "ryż", "marchew", "pietruszka"],
    naleśniki: ["mąka", "mleko", "jajka", "twaróg"],
  } as Record<string, string[]>,
} as const;

export function fillLine(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (_, k: string) => String(vars[k] ?? ""));
}

export function pickLine(key: LineKey, variant: number, vars: Record<string, string | number>): string {
  const list = LINES[key];
  return fillLine(list[Math.abs(variant) % list.length]!, vars);
}

export function joinPl(items: readonly string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} i ${items[items.length - 1]}`;
}
